import { CheckIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import type { ReactNode } from 'react'
import { pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button'
import { GhostButton } from '../buttons/ghost-button'
import { Spinner } from '../spinner.tsx'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './bulk-action-bar.css'

export type ActionButtonProps = {
  buttonText: string
  buttonType?: React.ComponentProps<typeof DefaultButton>['buttonType'] | 'ghost'
  icon?: ReactNode
  onAction: React.ComponentProps<typeof DefaultButton>['onClick']
  isDisabled?: boolean
  disabledTooltip?: ReactNode
  isPending?: boolean
}

type BulkActionBarProps = {
  actionButtonsProps: ActionButtonProps[]
  selectedCount: number
  selectedPageCount?: number
  onClear: () => unknown
}

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  actionButtonsProps,
  selectedCount,
  selectedPageCount,
  onClear,
}) => {
  const isPending = actionButtonsProps.some(a => a.isPending)

  return (
    <section
      aria-label="Bulk action"
      style={{
        position: 'sticky',
        bottom: '40px',
        margin: '0px',
        paddingTop: '0px',
        paddingBottom: '0px',
        marginTop: '40px',
      }}
      tabIndex={-1}
    >
      <div
        style={{
          paddingTop: '16px',
          paddingBottom: '16px',
          paddingLeft: '24px',
          paddingRight: '24px',
          minHeight: '56px',
          width: '100%',
        }}
        className={styles.box}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          className={styles.content}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckIcon aria-hidden="true" color={mappedColors.primary.nebula} size={24} weight="light" />
              <span className={styles.selectedText}>
                {selectedCount} selected
                {selectedPageCount !== undefined && selectedPageCount > 0 && (
                  <span className={styles.pageCountText}>
                    {' '}
                    ({selectedPageCount} {pluralize(selectedPageCount, 'page', 'pages')})
                  </span>
                )}
              </span>
            </div>
            {!isPending && (
              <button className={styles.clearButton} onClick={onClear}>
                Clear
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {actionButtonsProps.map(action => (
              <BulkActionButton key={action.buttonText} action={action} isAnyPending={isPending} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

type BulkActionButtonProps = {
  action: ActionButtonProps
  isAnyPending: boolean
}

const BulkActionButton: React.FC<BulkActionButtonProps> = ({ action, isAnyPending }) => {
  const {
    buttonText,
    buttonType = 'secondary',
    icon,
    onAction,
    isDisabled,
    disabledTooltip,
    isPending: isButtonPending,
  } = action
  const state = isButtonPending || isAnyPending || isDisabled ? 'disabled' : 'default'
  const button =
    buttonType === 'ghost' ? (
      <GhostButton
        size="small"
        type="secondary"
        icon={isButtonPending ? <Spinner label="Loading" size="auto" /> : icon}
        onClick={onAction}
        ariaLabel={buttonText}
        state={state}
      >
        {buttonText}
      </GhostButton>
    ) : (
      <DefaultButton
        size="small"
        buttonType={buttonType}
        icon={icon}
        onClick={onAction}
        aria-label={buttonText}
        state={isButtonPending ? 'loading' : isAnyPending || isDisabled ? 'disabled' : 'default'}
      >
        {buttonText}
      </DefaultButton>
    )

  if (isDisabled && disabledTooltip && !isAnyPending && !isButtonPending) {
    return (
      <TooltipWithHover trigger={<span style={{ display: 'inline-flex' }}>{button}</span>} side="top" maxWidth={300}>
        {disabledTooltip}
      </TooltipWithHover>
    )
  }

  return <span>{button}</span>
}
